/**
 * Solapa "Completadas" de Despacho — trazas de egreso con éxito (status='success').
 *
 * Datos reales del backend (GET /api/despacho/completadas), que lee la tabla
 * `traza_job` joineada con users y gln. El backend extrae los códigos de
 * transacción ANMAT del `result_payload` y los expone como un array.
 *
 * Refresh cada 30s para captar nuevas trazas que el poller del backend acabe
 * de marcar como `success`.
 */

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, RefreshCw, CheckCircle2, AlertTriangle, Search, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useDespachoCompletadas } from '@/api/queries';
import type { TrazaCompletada } from '@/api/contracts/despacho-completadas';
import type { TareaPendiente, TareaTipo } from '@/api/contracts/tareas';
import { CumplirTareaChip } from './CumplirTareaChip';

/**
 * Adapta una fila de Completadas al shape `TareaPendiente` para poder reusar
 * el `ComprobanteDetalleView` (que hoy espera ese shape). Mete el snapshot de
 * la traza en `traza` con `status='success'` así la vista muestra el badge
 * verde sin esperar a que el polling resuelva.
 */
function completadaToTarea(c: TrazaCompletada): TareaPendiente {
  const tipo: TareaTipo =
    c.tipoComprobante === 'remito_venta' ||
    c.tipoComprobante === 'destruccion' ||
    c.tipoComprobante === 'transferencia'
      ? c.tipoComprobante
      : 'otro';

  return {
    id: `completada-${c.id}`,
    tipo,
    sigla: c.sigla,
    tipoLabel: c.tipoLabel,
    movimiento: c.movimiento,
    claseComprobante: null,
    referencia: c.referencia,
    numeroRemito: c.numeroRemito,
    entidad: null,
    asunto: c.codigosTransaccion.length
      ? `Trazado · código ANMAT ${c.codigosTransaccion.join(', ')}`
      : null,
    descripcion: null,
    prioridad: null,
    solicitante: null,
    nombreSolicitante: null,
    responsable: null,
    nombreResponsable: null,
    fechaCreacion: c.finishedAt,
    fechaVencimiento: null,
    fechaAsignacion: null,
    traza: {
      id: c.id,
      jobId: c.jobId,
      status: 'success',
      totalItems: c.totalItems,
      doneItems: c.doneItems,
      errorItems: null,
      errorMessage: null,
      finishedAt: c.finishedAt,
      lastStatusCheckAt: null,
    },
  };
}

/** Color del chip por movimiento (prefijo de referencia): salida azul, destrucción roja. */
const BADGE_BY_MOVIMIENTO: Record<'salida' | 'destruccion', string> = {
  salida: 'badge-blue',
  destruccion: 'badge-red',
};

/** "2026-06-05T19:01:42.000Z" → "05/06/2026 16:01" (hora local). */
function fmtFechaHora(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}

interface Props {
  /** Callback que recibe la fila adaptada a TareaPendiente — DespachoPage la usa
   *  para abrir el mismo detalle que se ve desde Pendientes. */
  onAbrirTarea?: (tarea: TareaPendiente) => void;
}

export function CompletadasTrazadasList({ onAbrirTarea }: Props = {}) {
  const { data: items, isLoading, error, refetch, isFetching } = useDespachoCompletadas();
  const [searchParams] = useSearchParams();
  // Deep-link desde el dashboard: ?cierreErp=error abre el filtro ya activo.
  const [soloErrorCierre, setSoloErrorCierre] = useState(
    searchParams.get('cierreErp') === 'error',
  );
  // Filtro por tipo (sigla), igual que en Pendientes. null = todos.
  const [siglaFiltro, setSiglaFiltro] = useState<string | null>(null);
  // Búsqueda por texto: `texto` es lo que se tipea; `aplicada` lo que filtra
  // (se setea al apretar Buscar).
  const [textoBusqueda, setTextoBusqueda] = useState('');
  const [queryAplicada, setQueryAplicada] = useState('');

  const todas = useMemo(() => items ?? [], [items]);
  const conErrorCierre = useMemo(
    () => todas.filter((f) => f.cumplirTarea?.estado === 'error').length,
    [todas],
  );
  // Siglas presentes + conteo, para los chips de tipo.
  const siglasDisponibles = useMemo(() => {
    const counts = new Map<string, number>();
    for (const f of todas) {
      if (!f.sigla) continue;
      counts.set(f.sigla, (counts.get(f.sigla) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [todas]);
  const filas = useMemo(() => {
    const q = queryAplicada.trim().toLowerCase();
    let out = todas;
    if (soloErrorCierre) out = out.filter((f) => f.cumplirTarea?.estado === 'error');
    if (siglaFiltro) out = out.filter((f) => f.sigla === siglaFiltro);
    if (q) {
      out = out.filter((f) => {
        const remito = (f.numeroRemito ?? '').toLowerCase();
        const ref = (f.referencia ?? '').toLowerCase();
        return remito.includes(q) || ref.includes(q);
      });
    }
    return out;
  }, [todas, soloErrorCierre, siglaFiltro, queryAplicada]);
  const filtrando = soloErrorCierre || siglaFiltro !== null || queryAplicada.trim().length > 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-600">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        <span>Consultando trazas completadas...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          No se pudieron obtener las trazas completadas:{' '}
          {error instanceof Error ? error.message : String(error)}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {todas.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setQueryAplicada(textoBusqueda);
              }}
              className="flex flex-wrap items-center gap-2"
            >
              <div className="relative min-w-[200px] flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={textoBusqueda}
                  onChange={(e) => setTextoBusqueda(e.target.value)}
                  placeholder="Buscar por nº remito o referencia"
                  className="pl-9 font-mono"
                />
              </div>
              <Button
                type="submit"
                className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
              >
                <Search className="h-4 w-4" />
                <span className="ml-1.5">Buscar</span>
              </Button>
              {queryAplicada.trim().length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setTextoBusqueda('');
                    setQueryAplicada('');
                  }}
                  title="Limpiar búsqueda"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </form>

            {/* Filtro por tipo — chips dinámicos según las siglas presentes. */}
            {siglasDisponibles.length > 1 && (
              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <span className="mr-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                  Tipo
                </span>
                <TipoChip
                  label={`Todos (${todas.length})`}
                  active={siglaFiltro === null}
                  onClick={() => setSiglaFiltro(null)}
                />
                {siglasDisponibles.map(([sig, count]) => (
                  <TipoChip
                    key={sig}
                    label={`${sig} (${count})`}
                    active={siglaFiltro === sig}
                    onClick={() => setSiglaFiltro(sig)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-gray-600">
          {filtrando ? (
            <>
              {filas.length} de {todas.length} traza{todas.length === 1 ? '' : 's'} completada
              {todas.length === 1 ? '' : 's'}
            </>
          ) : (
            <>
              {todas.length} traza{todas.length === 1 ? '' : 's'} completada
              {todas.length === 1 ? '' : 's'} (status <code>success</code>)
            </>
          )}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSoloErrorCierre((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs transition',
              soloErrorCierre
                ? 'border-red-300 bg-red-50 text-red-700'
                : 'border-gray-200 text-gray-600 hover:border-red-300 hover:text-red-700',
            )}
            title="Filtrar los que fallaron al informar el cierre al ERP"
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            Error de cierre ERP
            {conErrorCierre > 0 && (
              <span className="ml-1 rounded-full bg-red-600 px-1.5 text-[10px] font-semibold text-white">
                {conErrorCierre}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-600 transition hover:border-everex-naranja hover:text-everex-azul disabled:opacity-50"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')} />
            Actualizar
          </button>
        </div>
      </div>

      {filas.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <CheckCircle2 className="h-6 w-6 text-gray-400" />
            </div>
            {soloErrorCierre ? (
              <>
                <h3 className="font-semibold text-everex-azul">Sin errores de cierre en el ERP</h3>
                <p className="mt-1 text-xs text-gray-600">
                  Ninguna traza completada quedó con error al informar el cierre al ERP.
                </p>
              </>
            ) : filtrando ? (
              <>
                <h3 className="font-semibold text-everex-azul">Sin resultados</h3>
                <p className="mt-1 text-xs text-gray-600">
                  Ninguna traza completada coincide con el filtro aplicado.
                </p>
              </>
            ) : (
              <>
                <h3 className="font-semibold text-everex-azul">Sin trazas completadas</h3>
                <p className="mt-1 text-xs text-gray-600">
                  Cuando trazás un comprobante con éxito, aparece acá con su código de transacción ANMAT.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Fecha trazado</th>
                    <th className="px-3 py-2 text-left">Tipo</th>
                    <th className="px-3 py-2 text-left">Referencia</th>
                    <th className="px-3 py-2 text-left">N° Remito</th>
                    <th className="px-3 py-2 text-right">Items</th>
                    <th className="px-3 py-2 text-left">GLN origen</th>
                    <th className="px-3 py-2 text-left">Usuario</th>
                    <th className="px-3 py-2 text-left">Código ANMAT</th>
                    <th className="px-3 py-2 text-left">Cierre ERP</th>
                  </tr>
                </thead>
                <tbody className="text-gray-800">
                  {filas.map((f) => (
                    <FilaCompletada
                      key={f.id}
                      fila={f}
                      onClick={onAbrirTarea ? () => onAbrirTarea(completadaToTarea(f)) : undefined}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/** Pill del filtro por tipo (sigla) — igual que en Pendientes. */
function TipoChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full border px-2.5 py-1 text-xs font-medium transition',
        active
          ? 'border-everex-azul bg-everex-azul text-white'
          : 'border-gray-200 text-gray-600 hover:border-everex-naranja hover:text-everex-azul',
      )}
    >
      {label}
    </button>
  );
}

function FilaCompletada({
  fila,
  onClick,
}: {
  fila: TrazaCompletada;
  onClick?: () => void;
}) {
  const badgeClass = BADGE_BY_MOVIMIENTO[fila.movimiento ?? 'salida'];
  const clickable = Boolean(onClick);
  return (
    <tr
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        'border-t border-gray-100',
        clickable && 'cursor-pointer transition hover:bg-orange-50',
      )}
    >
      <td className="px-3 py-2 whitespace-nowrap">{fmtFechaHora(fila.finishedAt)}</td>
      <td className="px-3 py-2">
        <span className={cn('badge', badgeClass)} title={fila.tipoLabel}>
          <b>{fila.sigla}</b>
        </span>
      </td>
      <td className="px-3 py-2">
        {fila.referencia}
        {fila.egresoAnulado && (
          <span
            className="ml-1.5 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700"
            title={`Egreso anulado el ${fmtFechaHora(fila.egresoAnulado.at)}${
              fila.egresoAnulado.por ? ` por ${fila.egresoAnulado.por}` : ''
            }`}
          >
            Egreso anulado
          </span>
        )}
        {fila.status === 'anulada' && !fila.egresoAnulado && (
          <span
            className="ml-1.5 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-700"
            title="El egreso de esta traza se anuló y el remito volvió a pendientes para trazarlo de nuevo"
          >
            Anulada · se vuelve a trazar
          </span>
        )}
        {fila.remitoAnulado && (
          <span
            className="ml-1.5 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700"
            title={`Remito anulado el ${fmtFechaHora(fila.remitoAnulado.at)}${
              fila.remitoAnulado.por ? ` por ${fila.remitoAnulado.por}` : ''
            }`}
          >
            Remito anulado
          </span>
        )}
      </td>
      <td className="px-3 py-2 font-semibold text-everex-azul">{fila.numeroRemito ?? '—'}</td>
      <td className="px-3 py-2 text-right">
        {fila.doneItems ?? '—'}
        {fila.totalItems != null && (
          <span className="ml-1 text-gray-500">/ {fila.totalItems}</span>
        )}
      </td>
      <td className="px-3 py-2">
        <b>{fila.gln.nombre}</b>{' '}
        <span className="text-[11px] text-gray-500">({fila.gln.gln})</span>
      </td>
      <td className="px-3 py-2">{fila.usuario.nombre}</td>
      <td className="px-3 py-2">
        {fila.codigosTransaccion.length === 0 ? (
          <span className="text-gray-400">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {fila.codigosTransaccion.map((c) => (
              <span
                key={c}
                className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-700"
                title={`Código de transacción ANMAT ${c}`}
              >
                {c}
              </span>
            ))}
          </div>
        )}
      </td>
      {/* El chip tiene su propio botón "(ver log)" — frenamos la propagación
          para que un click ahí no dispare la navegación al detalle de la fila. */}
      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
        <CumplirTareaChip
          estado={fila.cumplirTarea?.estado ?? null}
          fecha={fila.cumplirTarea?.fecha ?? null}
          erpId={fila.erpId ?? null}
        />
      </td>
    </tr>
  );
}
